import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Bar,
  Cell,
  Legend,
  Line,
  ComposedChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  DAILY_METRICS_VIEWS_COLOR,
  type DailyMetricsGrowthPctPoint,
} from "./dailyMetricsChart";

const PEAK_BAR_COLOR = "#4a3ab8";
const CUMULATIVE_LINE_COLOR = "#9b8ae8";

type Props = {
  data: DailyMetricsGrowthPctPoint[];
  height?: number;
};

function formatPct(value: number): string {
  return `${value.toFixed(1)}%`;
}

function formatViews(value: number): string {
  return Math.round(Number(value) || 0).toLocaleString("es-CL");
}

function GrowthPctTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ payload?: DailyMetricsGrowthPctPoint }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;

  const point = payload[0]?.payload as DailyMetricsGrowthPctPoint | undefined;
  if (!point) return null;

  const rows: Array<{ color: string; text: string }> = [
    {
      color: DAILY_METRICS_VIEWS_COLOR,
      text: `Views del período: ${formatViews(point.incrementalViews)}`,
    },
    {
      color: DAILY_METRICS_VIEWS_COLOR,
      text: `% crecimiento del total: ${formatPct(point.incrementalPct)}`,
    },
    {
      color: CUMULATIVE_LINE_COLOR,
      text: `Views acumuladas: ${formatViews(point.cumulativeViews)}`,
    },
    {
      color: CUMULATIVE_LINE_COLOR,
      text: `% acumulado del total: ${formatPct(point.cumulativePct)}`,
    },
  ];

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        px: 1.25,
        py: 0.75,
      }}
    >
      <Typography
        variant="caption"
        sx={{ display: "block", fontWeight: 700, color: "text.primary", mb: 0.5 }}
      >
        {label}
      </Typography>
      {rows.map((row) => (
        <Typography
          key={row.text}
          variant="caption"
          sx={{ display: "block", color: "text.primary", lineHeight: 1.45 }}
        >
          <Box
            component="span"
            sx={{
              display: "inline-block",
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: row.color,
              mr: 0.75,
              verticalAlign: "middle",
            }}
          />
          {row.text}
        </Typography>
      ))}
    </Box>
  );
}

export const DailyMetricsGrowthPctChart: React.FC<Props> = ({
  data,
  height = 300,
}) => {
  const theme = useTheme();

  return (
  <Box sx={{ width: "100%", height }}>
    <ResponsiveContainer>
      <ComposedChart
        data={data}
        margin={{ top: 8, right: 16, left: 4, bottom: 8 }}
      >
        <XAxis
          dataKey="label"
          tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
          tickLine={false}
          axisLine={{ stroke: alpha(theme.palette.divider, 0.3) }}
        />
        <YAxis
          yAxisId="pct"
          tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
          tickLine={false}
          axisLine={{ stroke: alpha(theme.palette.divider, 0.3) }}
          tickFormatter={(v) => `${Number(v).toFixed(0)}%`}
          domain={[0, "auto"]}
        />
        <RechartsTooltip content={<GrowthPctTooltip />} />
        <Legend
          formatter={(value) =>
            value === "incrementalPct"
              ? "% crecimiento del total"
              : "% acumulado del total"
          }
          wrapperStyle={{
            fontSize: 12,
            color: theme.palette.text.secondary,
          }}
        />
        <Bar
          yAxisId="pct"
          dataKey="incrementalPct"
          name="incrementalPct"
          radius={[4, 4, 0, 0]}
        >
          {data.map((entry) => (
            <Cell
              key={entry.label}
              fill={
                entry.isPeakIncremental
                  ? PEAK_BAR_COLOR
                  : DAILY_METRICS_VIEWS_COLOR
              }
            />
          ))}
        </Bar>
        <Line
          yAxisId="pct"
          type="monotone"
          dataKey="cumulativePct"
          name="cumulativePct"
          stroke={CUMULATIVE_LINE_COLOR}
          strokeWidth={2}
          dot={false}
          activeDot={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  </Box>
  );
};
